"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import { Menu, X, Phone, MapPin } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
const CLINIC_NAME = "Alpha Dental – Universal City";
const PHONE_NUMBER = "(210) 775-5177";
const PHONE_TEL = "tel:2107755177";
const ADDRESS_LINE = "916 Coronado Blvd, Universal City, TX 78148";
const LOGO_URL = "https://ddsalpha.com/wp-content/uploads/2026/05/cropped-Alpha-Logo-Website.webp";
const WP_HEADER_MENU_ID = "3";
const PINK_BTN = "bg-[#C27A7E] text-white hover:bg-[#A05D63] focus-visible:ring-2 focus-visible:ring-white/60";
const HEADER_BASE = "bg-gradient-to-r from-[#5C1F2A] via-[#7B2836] to-[#5C1F2A] text-white border-b border-[#7B2836]/50 shadow-[0_8px_32px_rgba(92,31,42,0.4)]";
const HEADER_SCROLLED = "shadow-[0_12px_48px_rgba(92,31,42,0.6)]";
type WPMenuItem = {
  id: string | number;
  label: string;
  url?: string;
};
function safeTrim(s?: string) {
  return (s || "").replace(/\s+/g, " ").trim();
}
function normalizeToRelativeHref(urlOrPath: string): string {
  const raw = (urlOrPath || "").trim();
  if (!raw) return "/";
  if (raw.startsWith("/") || raw.startsWith("#") || raw.startsWith("tel:") || raw.startsWith("mailto:")) {
    return raw;
  }
  try {
    const u = new URL(raw);
    if (typeof window !== "undefined") {
      const origin = window.location.origin;
      if (u.origin === origin) return `${u.pathname}${u.search}${u.hash}` || "/";
    }
    return raw;
  } catch {
    return raw.startsWith("/") ? raw : `/${raw}`;
  }
}
function getMenuHrefFromLabel(labelRaw: string): string {
  const label = safeTrim(labelRaw);
  const routeMap: Record<string, string> = {
    Home: "/",
    Services: "/services/",
    Blog: "/blog/",
    FAQs: "/faqs/",
    "Office Tour": "/office-tour/",
    Contact: "/contact-page/",
    "Contact Page": "/contact-page/"
  };
  return routeMap[label] || "/";
}
function getMenuHref(item: WPMenuItem): string {
  const label = safeTrim(item.label);
  if (item.url) {
    const normalized = normalizeToRelativeHref(item.url);
    if (normalized === "/" && label && label !== "Home") return getMenuHrefFromLabel(label);
    return normalized;
  }
  return getMenuHrefFromLabel(label);
}
const HIDDEN_MENU_LABELS = new Set(["Schedule An Appointment", "Schedule an Appointment", "Schedule Appointment", "Schedule", "Book Appointment", "Book an Appointment"].map(s => safeTrim(s).toLowerCase()));
function shouldHideMenuItem(item: WPMenuItem): boolean {
  const label = safeTrim(item.label).toLowerCase();
  if (HIDDEN_MENU_LABELS.has(label)) return true;
  const url = (item.url || "").toLowerCase();
  if (url.includes("appointnow.com")) return true;
  return false;
}
const FALLBACK_MENU: WPMenuItem[] = [{
  id: "fb-home",
  label: "Home",
  url: "/"
}, {
  id: "fb-services",
  label: "Services",
  url: "/services/"
}, {
  id: "fb-blog",
  label: "Blog",
  url: "/blog/"
}, {
  id: "fb-faqs",
  label: "FAQs",
  url: "/faqs/"
}, {
  id: "fb-office-tour",
  label: "Office Tour",
  url: "/office-tour/"
}, {
  id: "fb-contact",
  label: "Contact",
  url: "/contact-page/"
}];
function DesktopMenu({
  items,
  loading
}: {
  items: WPMenuItem[];
  loading: boolean;
}) {
  if (loading) {
    return <div className="hidden lg:flex gap-2">
        {[1, 2, 3, 4, 5].map(i => <div key={i} className="h-9 w-20 bg-white/10 animate-pulse rounded-md" />)}
      </div>;
  }
  return <NavigationMenu viewport={false} className="hidden lg:flex">
      <NavigationMenuList className="gap-1">
        {items.map((item, i) => {
        const href = getMenuHref(item);
        const label = safeTrim(item.label) || "Menu item";
        return <NavigationMenuItem key={item.id} data-index={i}>
              <NavigationMenuLink asChild>
                <Link to={href} className="group relative px-4 py-2 rounded-md text-white/95 text-sm font-semibold transition-colors duration-200 hover:!bg-white/10 hover:text-white focus:!bg-white/10 focus:text-white" aria-label={label}>
                  {label}
                  <span className="absolute bottom-0 left-1/2 h-0.5 w-0 bg-white/80 transition-all duration-300 ease-out group-hover:left-0 group-hover:w-full group-focus:left-0 group-focus:w-full" />
                </Link>
              </NavigationMenuLink>
            </NavigationMenuItem>;
      })}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenu({
  isOpen,
  onClose,
  items,
  loading
}: {
  isOpen: boolean;
  onClose: () => void;
  items: WPMenuItem[];
  loading: boolean;
}) {
  if (!isOpen) return null;
  return <div className="fixed inset-0 z-[9999] lg:hidden">
      {}
      <div className="absolute inset-0 bg-black/75" onClick={onClose} aria-hidden="true" />

      {}
      <div className="absolute top-0 right-0 h-full w-4/5 max-w-sm bg-[#5C1F2A] border-l border-white/10 shadow-strong">
        {}
        <div className="flex items-center justify-between px-6 py-5 border-b border-white/10">
          <span className="text-white font-semibold text-lg">Menu</span>
          <Button variant="ghost" size="icon" onClick={onClose} className="text-white hover:!bg-white/10 hover:text-white focus:!bg-white/10" aria-label="Close menu">
            <X className="size-5" />
          </Button>
        </div>

        {}
        <div className="px-6 pt-4 pb-2 space-y-2">
          <div className="flex items-start gap-2 text-white text-sm font-semibold">
            <MapPin className="size-4 mt-0.5" />
            <span>{ADDRESS_LINE}</span>
          </div>

          <Link to={PHONE_TEL} className="inline-flex items-center gap-2 text-white text-sm font-semibold underline underline-offset-4">
            <Phone className="size-4" />
            {PHONE_NUMBER}
          </Link>
        </div>

        {}
        <nav className="flex flex-col gap-1 p-4">
          {loading ? <>
              {[1, 2, 3, 4, 5].map(i => <div key={i} className="h-12 bg-white/10 animate-pulse rounded-md" />)}
            </> : items.map((item, i) => {
          const href = getMenuHref(item);
          const label = safeTrim(item.label) || "Menu item";
          return <Link key={item.id} data-index={i} to={href} onClick={onClose} className="px-4 py-3 text-white text-base font-semibold rounded-md hover:bg-white/10 hover:text-white focus:bg-white/10 focus:text-white transition-colors duration-150">
                  {label}
                </Link>;
        })}

          {}
          <div className="flex flex-col gap-3 mt-6 px-4">
            <Button asChild className={`w-full justify-center ${PINK_BTN}`}>
              <Link to={PHONE_TEL} className="flex items-center gap-2">
                <Phone className="size-4" />
                Call Now
              </Link>
            </Button>
          </div>
        </nav>
      </div>
    </div>;
}
function MenuConsumer({
  onResolved
}: {
  onResolved: (items: WPMenuItem[], loading: boolean) => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu() as {
    menuItems: WPMenuItem[];
    loading: boolean;
  };
  useEffect(() => {
    const visible = (menuItems || []).filter(it => !shouldHideMenuItem(it));
    onResolved(visible, loading);
  }, [menuItems, loading, onResolved]);
  return null;
}
export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [resolvedItems, setResolvedItems] = useState<WPMenuItem[] | null>(null);
  const [wpLoading, setWpLoading] = useState(true);
  const handleMenuResolved = useCallback((items: WPMenuItem[], loading: boolean) => {
    setWpLoading(loading);
    setResolvedItems(prev => {
      if (loading) return prev;
      if (items && items.length > 0) return items;
      return null;
    });
  }, []);
  const jsonLd = useMemo(() => {
    const schema = {
      "@context": "https://schema.org",
      "@type": "Dentist",
      name: CLINIC_NAME,
      telephone: "+1-210-775-5177",
      url: "https://ddsalpha.com/",
      image: LOGO_URL,
      address: {
        "@type": "PostalAddress",
        streetAddress: "916 Coronado Blvd",
        addressLocality: "Universal City",
        addressRegion: "TX",
        postalCode: "78148",
        addressCountry: "US"
      },
      areaServed: ["Universal City TX", "Live Oak TX", "Converse TX", "Selma TX", "Schertz TX", "San Antonio TX", "Randolph AFB"],
      sameAs: []
    };
    return JSON.stringify(schema);
  }, []);
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    if (typeof document === "undefined") return;
    const body = document.body;
    if (mobileMenuOpen) body.classList.add("overflow-hidden");else body.classList.remove("overflow-hidden");
    return () => body.classList.remove("overflow-hidden");
  }, [mobileMenuOpen]);
  const itemsToRender = resolvedItems && resolvedItems.length > 0 ? resolvedItems : FALLBACK_MENU;
  return <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: jsonLd
    }} />

      <header className={["sticky top-0 z-40 w-full", HEADER_BASE, "transition-all duration-200", scrolled ? `${HEADER_SCROLLED} h-[84px]` : "h-[96px]"].join(" ")} aria-label={`${CLINIC_NAME} site header`}>
        <div className="container mx-auto px-4 h-full flex items-center justify-between gap-4">
          {}
          <Link to="/" className="flex items-center gap-3 hover:opacity-95 transition-opacity duration-150" aria-label="Go to homepage">
            <img src={LOGO_URL} alt={`${CLINIC_NAME} logo`} className="h-10 w-auto sm:h-12 max-h-12" loading="eager" fetchpriority="high" />
            <div className="hidden md:block leading-tight">
              <div className="text-white font-bold text-sm md:text-base">
                {CLINIC_NAME}
              </div>
              <div className="text-white/80 text-xs font-semibold">
                {ADDRESS_LINE}
              </div>
            </div>
          </Link>

          {}
          <MenuProvider menu_id={WP_HEADER_MENU_ID} className="flex-1 flex justify-center">
            <MenuConsumer onResolved={handleMenuResolved} />

            <DesktopMenu items={itemsToRender} loading={wpLoading && !resolvedItems} />
          </MenuProvider>

          {}
          <div className="flex items-center gap-3">
            <div className="hidden xl:flex flex-col items-end leading-tight">
              <Link to={PHONE_TEL} className="text-white font-bold text-sm hover:underline underline-offset-4" aria-label={`Call ${PHONE_NUMBER}`}>
                {PHONE_NUMBER}
              </Link>
              <div className="text-white/80 text-xs font-semibold">
                Near Randolph Air Force Base
              </div>
            </div>

            <div className="hidden lg:flex items-center gap-3">
              <Button asChild size="sm" className={PINK_BTN}>
                <Link to={PHONE_TEL} className="flex items-center gap-2">
                  <Phone className="size-4" />
                  Call
                </Link>
              </Button>
            </div>

            {}
            <Button variant="ghost" size="icon" className="lg:hidden text-white hover:!bg-white/10 hover:text-white focus:!bg-white/10" onClick={() => setMobileMenuOpen(true)} aria-label="Open menu">
              <Menu className="size-6" />
            </Button>
          </div>
        </div>

        {}
        <MobileMenu isOpen={mobileMenuOpen} onClose={() => setMobileMenuOpen(false)} items={itemsToRender} loading={wpLoading && !resolvedItems} />
      </header>
    </>;
}